import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import React, { ReactElement, FC } from 'react';
import { t } from 'react-i18nify';
import { useAppSelector } from '../hooks';
import { IComponentGroup, IConfiguration, IHint } from '../Interface/Interface';
import ConfigurationItem from './Item';
import { black, red } from '../Constants/Colors';
import { makeStyles } from 'tss-react/mui';
import { getOptionLabel } from '../Utils/getOptionLabel';

interface IConfigurationInfo {
  // eslint-disable-next-line no-unused-vars
  onImageLoaded?: (width: number, height: number) => void;
}

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      padding: '0px !important',
      [theme.breakpoints.up('md')]: {
        overflow: 'inherit !important',
        width: '100%'
      },
      [theme.breakpoints.down('sm')]: {
        padding: '16px !important',
        width: 'fit-content !important'
      }
    },
    line: {
      height: 1,
      background: black,
      marginTop: 16,
      marginBottom: 16
    },
    hints: {
      color: `${red}!important`,
      fontWeight: 'bold !important'
    },
    componentGroup: {
      borderBottom: 'none !important',
      textTransform: 'uppercase',
      paddingLeft: '0px !important'
    },
    componentTitle: {
      fontSize: '14px !important',
      fontWeight: 'bold !important',
      borderBottom: 'none !important',
      padding: '8px 16px 8px 0px  !important',
      paddingLeft: '0px !important'
    },
    selectedOptions: {
      fontSize: '14px',
      borderBottom: 'none !important',
      padding: '8px 0px 8px 16px !important',
      whiteSpace: 'pre-wrap'
    }
  };
});

const ConfigurationInfo: FC<IConfigurationInfo> = ({
  onImageLoaded
}): ReactElement | null => {
  const configuration: IConfiguration | undefined = useAppSelector(
    (state) => state.configuration.configuration
  );
  const groupedComponents: IComponentGroup[] | undefined = useAppSelector(
    (state) => state.configuration.groupedComponents
  );
  const hints: IHint[] | undefined = useAppSelector(
    (state) => state.configuration.hints
  );

  const { classes } = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  if (configuration) {
    return (
      <TableContainer className={classes.root}>
        <ConfigurationItem
          item={configuration.item}
          onImageLoaded={onImageLoaded}
        />
        {hints && hints.length > 0 && isMobile && (
          <>
            <div className={classes.line} />
            <Box sx={{ pt: 2, pb: 2 }}>
              <Typography variant="h3" className={classes.hints}>
                {t('verify')}
              </Typography>
              {hints.map((hint: IHint, i: number) => (
                <Typography variant="h4" className={classes.hints} key={i}>
                  {t(hint.message)}
                </Typography>
              ))}
            </Box>
          </>
        )}

        <Table>
          <TableBody>
            {groupedComponents &&
              groupedComponents.map((componentGroup: IComponentGroup, i) => {
                const components = componentGroup.components;
                return (
                  <React.Fragment key={i}>
                    {components.map((component, i) => {
                      if (component.hiddenInFrontend) {
                        return null;
                      } else {
                        return (
                          <TableRow key={i}>
                            <TableCell
                              className={classes.componentTitle}
                              style={{ verticalAlign: 'top' }}>
                              {component?.title}
                            </TableCell>
                            {component.selectedoptions?.length ? (
                              <TableCell
                                key={i}
                                className={classes.selectedOptions}>
                                {component.selectedoptions.map((option) => (
                                  <React.Fragment key={option.identifier}>
                                    {getOptionLabel(option)}
                                    <br />
                                  </React.Fragment>
                                ))}
                              </TableCell>
                            ) : (
                              <TableCell className={classes.selectedOptions}>
                                {t('noSelection')}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                    })}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>

        {hints && hints.length > 0 && !isMobile && (
          <>
            <div className={classes.line} />
            <Box sx={{ pt: 2, pb: 2 }}>
              <Typography variant="h3" className={classes.hints}>
                {t('verify')}
              </Typography>
              {hints.map((hint: IHint, i: number) => (
                <Typography variant="h4" className={classes.hints} key={i}>
                  {t(hint.message)}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </TableContainer>
    );
  }
  return null;
};

export default ConfigurationInfo;
